import React, {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect
} from 'react';
import { Box, IconButton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  TableV3FilterItem,
  TableV3FilterOptions
} from 'common/table/types/TableV3';
import { DropdownList } from 'system';
import { useTableFilterSelection } from 'common/table/hooks/useTableFilterSelection';
import { useTableFilterApply } from 'common/table/hooks/useTableFilterApply';
import { TableV3FilterSelectionContext } from 'common/table/components/TableV3FilterSelectionContext';
import { FILTER_DROPDOWN_MAX_HEIGHT } from 'common/table/styles/filterDropdown';
import { pinSelectedFirst } from 'common/table/utils/pinSelectedFirst';

type TableV3FilterButtonProps = {
  filterOptions: TableV3FilterOptions;
  isSorted?: boolean;
  onFilterSelect: (item: TableV3FilterItem) => void;
  onFilterApply?: (items: TableV3FilterItem[]) => void;
};

export type TableV3FilterButtonRef = {
  openFilterDialog: () => void;
};

export const TableV3FilterButton = forwardRef<
  TableV3FilterButtonRef,
  TableV3FilterButtonProps
>(
  (
    { filterOptions, isSorted = false, onFilterSelect, onFilterApply },
    ref
  ): React.ReactElement => {
    const [searchQuery, setSearchQuery] = useState('');
    const anchorRef = useRef<HTMLButtonElement>(null);

    const committedValues = filterOptions.selectedValues as string[];
    const requiresApply = filterOptions.requiresApply ?? false;

    const { selected, toggle, setSelection } = useTableFilterSelection(
      committedValues,
      filterOptions.selectionType
    );

    const getSelectedItems = () =>
      selected.map((value) => ({
        value,
        label:
          filterOptions.items.find((item) => item.value === value)?.label ??
          filterOptions.selectedItems?.find((item) => item.value === value)
            ?.label ??
          value
      }));

    const { open, setOpen, deferral, confirmActions, toggleOpen, dismiss } =
      useTableFilterApply({
        requiresApply,
        committedValues,
        getSelectedItems,
        setSelection,
        onFilterApply
      });

    useImperativeHandle(ref, () => ({
      openFilterDialog: () => setOpen(true)
    }));

    // Each close forgets the search, so the next open shows the full list.
    useEffect(() => {
      if (!open) setSearchQuery('');
    }, [open]);

    // Applied values the option list does not include still go on top.
    const missingSelectedItems = (filterOptions.selectedItems ?? []).filter(
      (selectedItem) =>
        committedValues.includes(selectedItem.value) &&
        !filterOptions.items.some((item) => item.value === selectedItem.value)
    );
    const allItems = [...missingSelectedItems, ...filterOptions.items];

    const onFilterClick = (value: string) => {
      const item = allItems.find((i) => i.value === value);
      if (!item) return;

      toggle(value);
      if (!requiresApply) onFilterSelect(item);
    };

    const filteredItems = pinSelectedFirst(
      searchQuery
        ? allItems.filter((item) =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : allItems,
      committedValues
    );

    return (
      <Box>
        <IconButton
          ref={anchorRef}
          onClick={toggleOpen}
          sx={{
            color: isSorted ? 'customGrey.light' : 'customGrey.dark'
          }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
        <DropdownList
          popperProps={{
            open,
            sx: {
              zIndex: 2
            },
            anchorEl: anchorRef.current,
            placement: 'bottom-end',
            modifiers: [
              {
                name: 'flip',
                enabled: false
              },
              {
                name: 'preventOverflow',
                options: {
                  padding: 32
                }
              }
            ]
          }}
          headerContent={
            filterOptions.headerContent && (
              <TableV3FilterSelectionContext.Provider
                value={{ setSelection, getSelectedItems, deferral }}
              >
                {filterOptions.headerContent}
              </TableV3FilterSelectionContext.Provider>
            )
          }
          maxHeight={FILTER_DROPDOWN_MAX_HEIGHT}
          showCheckboxes={filterOptions.showCheckboxes}
          showSelectedCount={requiresApply}
          wrapLabels
          confirmActions={confirmActions}
          searchOptions={
            filterOptions.isSearchable
              ? {
                  searchQuery,
                  onChange: setSearchQuery
                }
              : undefined
          }
          options={filteredItems.map((item) => ({
            value: item.value as string,
            label: item.label
          }))}
          selectedValues={selected}
          onSelect={onFilterClick}
          onClickAway={(event) => dismiss(event, anchorRef.current)}
        />
      </Box>
    );
  }
);
